import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import io from 'socket.io-client';
import {
  Box, Paper, Typography, Stepper, Step, StepLabel, StepContent,
  Button, CircularProgress, Chip, Avatar, Divider, Dialog,
  DialogTitle, DialogContent, DialogActions, TextField, Rating,
  IconButton, Alert, Card, CardContent, Grid
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import StarIcon from '@mui/icons-material/Star';
import { MapContainer, TileLayer, Marker, Popup, Polyline } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Fix Leaflet default marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
});

const OrderTrackingPage = () => {
  const { storeSlug, orderId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState(null);
  const [error, setError] = useState('');
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancellationReason, setCancellationReason] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const [ratingDialogOpen, setRatingDialogOpen] = useState(false);
  const [productRating, setProductRating] = useState(0);
  const [productReview, setProductReview] = useState('');
  const [deliveryRating, setDeliveryRating] = useState(0);
  const [deliveryReview, setDeliveryReview] = useState('');
  const [submittingRating, setSubmittingRating] = useState(false);
  const [deliveryLocation, setDeliveryLocation] = useState(null);
  const socketRef = useRef(null);
  const mapRef = useRef(null);

  const statusSteps = [
    { key: 'PENDING', label: 'Order Placed', icon: <AccessTimeIcon /> },
    { key: 'CONFIRMED', label: 'Confirmed', icon: <CheckCircleIcon /> },
    { key: 'PREPARING', label: 'Preparing', icon: <RestaurantIcon /> },
    { key: 'READY', label: 'Ready', icon: <CheckCircleIcon /> },
    { key: 'OUT_FOR_DELIVERY', label: 'Out for Delivery', icon: <LocalShippingIcon /> },
    { key: 'DELIVERED', label: 'Delivered', icon: <CheckCircleIcon /> }
  ];

  const getActiveStep = (status) => {
    if (status === 'CANCELLED') return -1;
    const index = statusSteps.findIndex(s => s.key === status);
    return index >= 0 ? index : 0;
  };

  useEffect(() => {
    loadOrderStatus();

    // Setup Socket.io connection
    const socket = io(axios.defaults.baseURL || 'http://localhost:3100');
    socketRef.current = socket;

    // Listen for order status updates
    socket.on(`order-${orderId}`, (data) => {
      console.log('Order status update received:', data);
      setOrder(prev => ({
        ...prev,
        status: data.status,
        statusHistory: data.statusHistory,
        deliveryPerson: data.deliveryPerson || prev.deliveryPerson
      }));
    });

    // Listen for GPS location updates
    socket.on(`order-location-${orderId}`, (data) => {
      console.log('GPS location update received:', data);
      setDeliveryLocation({
        latitude: data.latitude,
        longitude: data.longitude,
        timestamp: data.timestamp
      });
      setOrder(prev => ({
        ...prev,
        deliveryPerson: {
          ...prev.deliveryPerson,
          currentLatitude: data.latitude,
          currentLongitude: data.longitude,
          lastLocationUpdate: data.timestamp
        }
      }));
    });

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
      }
    };
  }, [orderId]);

  const loadOrderStatus = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/ecommerce-store/public/${storeSlug}/order/${orderId}/status`);
      if (response.data.success) {
        setOrder(response.data.data);
        if (response.data.data.deliveryPerson?.currentLatitude) {
          setDeliveryLocation({
            latitude: response.data.data.deliveryPerson.currentLatitude,
            longitude: response.data.data.deliveryPerson.currentLongitude,
            timestamp: response.data.data.deliveryPerson.lastLocationUpdate
          });
        }
      } else {
        setError(response.data.message || 'Failed to load order');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load order status');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelOrder = async () => {
    if (!cancellationReason.trim()) {
      alert('Please provide a reason for cancellation');
      return;
    }

    try {
      setCancelling(true);
      const response = await axios.post(
        `/api/ecommerce-store/public/${storeSlug}/order/${orderId}/cancel`,
        { cancellationReason }
      );
      if (response.data.success) {
        setCancelDialogOpen(false);
        loadOrderStatus();
      } else {
        alert(response.data.message || 'Failed to cancel order');
      }
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to cancel order');
    } finally {
      setCancelling(false);
    }
  };

  const handleSubmitRating = async () => {
    if (productRating === 0 && deliveryRating === 0) {
      alert('Please provide at least one rating');
      return;
    }

    try {
      setSubmittingRating(true);
      const response = await axios.post(
        `/api/ecommerce-store/public/${storeSlug}/order/${orderId}/rate`,
        {
          productRating: productRating || undefined,
          productReview,
          deliveryRating: deliveryRating || undefined,
          deliveryReview
        }
      );
      if (response.data.success) {
        setRatingDialogOpen(false);
        loadOrderStatus();
        alert('Thank you for your feedback!');
      } else {
        alert(response.data.message || 'Failed to submit rating');
      }
    } catch (err) {
      alert(err.response?.data?.message || 'Failed to submit rating');
    } finally {
      setSubmittingRating(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !order) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', p: 3 }}>
        <Typography variant="h6" color="error" gutterBottom>{error || 'Order not found'}</Typography>
        <Button variant="contained" onClick={() => navigate(-1)} sx={{ mt: 2 }}>
          Go Back
        </Button>
      </Box>
    );
  }

  const activeStep = getActiveStep(order.status);
  const isCancelled = order.status === 'CANCELLED';
  const isDelivered = order.status === 'DELIVERED';
  const canCancel = order.canCancel && order.status === 'PENDING';
  const canRate = isDelivered && !order.productRating && !order.deliveryRating;
  const showMap = order.status === 'OUT_FOR_DELIVERY' && 
                  order.deliveryPerson?.currentLatitude && 
                  order.customer?.latitude;

  return (
    <Box sx={{ bgcolor: '#f5f5f5', minHeight: '100vh', pb: 4 }}>
      {/* Header */}
      <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton onClick={() => navigate(-1)}>
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" fontWeight={700}>
              Track Order
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Order ID: {orderId.slice(-8).toUpperCase()}
            </Typography>
          </Box>
          {isCancelled ? (
            <Chip label="CANCELLED" color="error" icon={<CancelIcon />} />
          ) : isDelivered ? (
            <Chip label="DELIVERED" color="success" icon={<CheckCircleIcon />} />
          ) : (
            <Chip label={order.status} color="primary" />
          )}
        </Box>
      </Paper>

      <Box sx={{ maxWidth: 1200, mx: 'auto', px: 2 }}>
        <Grid container spacing={3}>
          {/* Left Column - Order Status */}
          <Grid item xs={12} md={showMap ? 6 : 12}>
            {/* Status Timeline */}
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>
                Order Status
              </Typography>
              <Divider sx={{ mb: 3 }} />

              {isCancelled ? (
                <Alert severity="error" sx={{ mb: 2 }}>
                  <Typography variant="body2" fontWeight={600}>
                    Order Cancelled
                  </Typography>
                  {order.cancellationReason && (
                    <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
                      Reason: {order.cancellationReason}
                    </Typography>
                  )}
                </Alert>
              ) : (
                <Stepper activeStep={activeStep} orientation="vertical">
                  {statusSteps.map((step, index) => (
                    <Step key={step.key}>
                      <StepLabel
                        StepIconComponent={() => (
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: '50%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              bgcolor: index <= activeStep ? 'primary.main' : 'grey.300',
                              color: 'white'
                            }}
                          >
                            {step.icon}
                          </Box>
                        )}
                      >
                        <Typography variant="body1" fontWeight={600}>
                          {step.label}
                        </Typography>
                      </StepLabel>
                      <StepContent>
                        {order.statusHistory
                          ?.filter(h => h.status === step.key)
                          .map((history, idx) => (
                            <Box key={idx} sx={{ mb: 1 }}>
                              <Typography variant="caption" color="text.secondary">
                                {formatDate(history.timestamp)}
                              </Typography>
                              {history.note && (
                                <Typography variant="body2" sx={{ mt: 0.5 }}>
                                  {history.note}
                                </Typography>
                              )}
                            </Box>
                          ))}
                      </StepContent>
                    </Step>
                  ))}
                </Stepper>
              )}
            </Paper>

            {/* Order Details */}
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" fontWeight={700} gutterBottom>
                Order Details
              </Typography>
              <Divider sx={{ mb: 2 }} />

              {order.items?.map((item, idx) => (
                <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1.5 }}>
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      {item.name} x {item.qty}
                    </Typography>
                    {item.variant && (
                      <Typography variant="caption" color="text.secondary">
                        {item.variant}
                      </Typography>
                    )}
                  </Box>
                  <Typography variant="body2" fontWeight={600}>
                    ${(item.price * item.qty).toFixed(2)}
                  </Typography>
                </Box>
              ))}

              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">Subtotal</Typography>
                <Typography variant="body2">${order.total?.toFixed(2)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700 }}>
                <Typography variant="body1">Total</Typography>
                <Typography variant="body1">${order.total?.toFixed(2)}</Typography>
              </Box>
            </Paper>

            {/* Customer Information */}
            {order.customer && (
              <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" fontWeight={700} gutterBottom>
                  Delivery Information
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                  {order.customer.name && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Customer Name
                      </Typography>
                      <Typography variant="body1" fontWeight={600}>
                        {order.customer.name}
                      </Typography>
                    </Box>
                  )}
                  {order.customer.phone && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Phone Number
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <PhoneIcon fontSize="small" color="action" />
                        <Typography variant="body1">
                          {order.customer.phone}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                  {order.customer.address && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Delivery Address
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                        <LocationOnIcon fontSize="small" color="action" />
                        <Typography variant="body1">
                          {order.customer.address}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                  {order.paymentMode && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Payment Method
                      </Typography>
                      <Typography variant="body1">
                        {order.paymentMode === 'ONLINE' ? 'Online Payment' : 'Pay at Counter'}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Paper>
            )}

            {/* Delivery Person Info */}
            {order.deliveryPerson?.name && (
              <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" fontWeight={700} gutterBottom>
                  Delivery Person
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar sx={{ width: 56, height: 56, bgcolor: 'primary.main' }}>
                    {order.deliveryPerson.name.charAt(0)}
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body1" fontWeight={600}>
                      {order.deliveryPerson.name}
                    </Typography>
                    {order.deliveryPerson.phone && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                        <PhoneIcon fontSize="small" color="action" />
                        <Typography variant="body2" color="text.secondary">
                          {order.deliveryPerson.phone}
                        </Typography>
                      </Box>
                    )}
                    {deliveryLocation && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                        Last updated: {formatDate(deliveryLocation.timestamp)}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Paper>
            )}

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              {canCancel && (
                <Button
                  variant="outlined"
                  color="error"
                  fullWidth
                  onClick={() => setCancelDialogOpen(true)}
                >
                  Cancel Order
                </Button>
              )}
              {canRate && (
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  startIcon={<StarIcon />}
                  onClick={() => setRatingDialogOpen(true)}
                >
                  Rate Order
                </Button>
              )}
            </Box>
          </Grid>

          {/* Right Column - Map */}
          {showMap && (
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3, height: '100%', minHeight: 600 }}>
                <Typography variant="h6" fontWeight={700} gutterBottom>
                  Live Tracking
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <Box sx={{ height: 500, borderRadius: 2, overflow: 'hidden' }}>
                  <MapContainer
                    center={[order.customer.latitude, order.customer.longitude]}
                    zoom={13}
                    style={{ height: '100%', width: '100%' }}
                    ref={mapRef}
                  >
                    <TileLayer
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    />
                    
                    {/* Customer Location */}
                    <Marker position={[order.customer.latitude, order.customer.longitude]}>
                      <Popup>
                        <Typography variant="body2" fontWeight={600}>
                          Delivery Address
                        </Typography>
                        <Typography variant="caption">
                          {order.customer.address}
                        </Typography>
                      </Popup>
                    </Marker>

                    {/* Delivery Person Location */}
                    {order.deliveryPerson.currentLatitude && (
                      <Marker
                        position={[
                          order.deliveryPerson.currentLatitude,
                          order.deliveryPerson.currentLongitude
                        ]}
                        icon={L.icon({
                          iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png',
                          shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
                          iconSize: [25, 41],
                          iconAnchor: [12, 41],
                          popupAnchor: [1, -34],
                          shadowSize: [41, 41]
                        })}
                      >
                        <Popup>
                          <Typography variant="body2" fontWeight={600}>
                            {order.deliveryPerson.name}
                          </Typography>
                          <Typography variant="caption">
                            Delivery Person
                          </Typography>
                        </Popup>
                      </Marker>
                    )}

                    {/* Route Line */}
                    {order.deliveryPerson.currentLatitude && (
                      <Polyline
                        positions={[
                          [order.deliveryPerson.currentLatitude, order.deliveryPerson.currentLongitude],
                          [order.customer.latitude, order.customer.longitude]
                        ]}
                        color="blue"
                        weight={3}
                        opacity={0.7}
                        dashArray="10, 10"
                      />
                    )}
                  </MapContainer>
                </Box>
              </Paper>
            </Grid>
          )}
        </Grid>
      </Box>

      {/* Cancel Order Dialog */}
      <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Cancel Order</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Please provide a reason for cancelling this order:
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            value={cancellationReason}
            onChange={(e) => setCancellationReason(e.target.value)}
            placeholder="e.g., Changed my mind, Ordered by mistake, etc."
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialogOpen(false)}>
            Keep Order
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleCancelOrder}
            disabled={cancelling || !cancellationReason.trim()}
          >
            {cancelling ? <CircularProgress size={24} /> : 'Cancel Order'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rating Dialog */}
      <Dialog open={ratingDialogOpen} onClose={() => setRatingDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Rate Your Order</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Product Quality
            </Typography>
            <Rating
              value={productRating}
              onChange={(e, newValue) => setProductRating(newValue)}
              size="large"
            />
            <TextField
              fullWidth
              multiline
              rows={2}
              value={productReview}
              onChange={(e) => setProductReview(e.target.value)}
              placeholder="Tell us about the product quality..."
              sx={{ mt: 1 }}
            />
          </Box>

          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Delivery Service
            </Typography>
            <Rating
              value={deliveryRating}
              onChange={(e, newValue) => setDeliveryRating(newValue)}
              size="large"
            />
            <TextField
              fullWidth
              multiline
              rows={2}
              value={deliveryReview}
              onChange={(e) => setDeliveryReview(e.target.value)}
              placeholder="Tell us about the delivery service..."
              sx={{ mt: 1 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRatingDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmitRating}
            disabled={submittingRating || (productRating === 0 && deliveryRating === 0)}
          >
            {submittingRating ? <CircularProgress size={24} /> : 'Submit Rating'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OrderTrackingPage;
